import { UserButton, useUser } from '@clerk/clerk-react'
import { Icon, type IconName } from '../icon'
import { IconButton } from '../button'
import { T } from '../../data/mock'
import { useMe, type Role } from '../../lib/use-me'
import { canViewExecutive, roleLabel } from '../../lib/permissions'

export type NavId =
  | 'dashboard'
  | 'transactions'
  | 'forecast'
  | 'cashflow'
  | 'salaries'
  | 'planning'
  | 'licenses'
  | 'invoices'
  | 'settings'

export type NavItem = {
  id: NavId
  icon: IconName
  label: string
  badge?: string
  badgeKind?: 'danger'
  minRole?: Role
}

/** Shared by the desktop top nav and the mobile off-canvas nav (inside the rail). */
export const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', icon: 'layout-dashboard', label: T.nav.dashboard },
  { id: 'transactions', icon: 'arrow-left-right', label: T.nav.transactions },
  { id: 'forecast', icon: 'trending-up', label: T.nav.forecast, minRole: 'clevel' },
  { id: 'cashflow', icon: 'pie-chart', label: T.nav.cashflow, minRole: 'clevel' },
  { id: 'salaries', icon: 'banknote', label: T.nav.salaries },
  { id: 'planning', icon: 'calendar-clock', label: 'Планування' },
  { id: 'licenses', icon: 'key-round', label: T.nav.licenses, minRole: 'clevel' },
  { id: 'invoices', icon: 'receipt', label: 'Інвойси' },
  { id: 'settings', icon: 'settings', label: T.nav.settings, minRole: 'clevel' },
]

/** Nav items visible to the given role. `minRole: 'clevel'` items hide from `user`. */
export function visibleNavItems(role?: Role): NavItem[] {
  return NAV_ITEMS.filter((it) => !it.minRole || canViewExecutive(role))
}

type Props = {
  active: NavId
  onNavigate: (id: NavId) => void
  onCmdK?: () => void
  /** Toggles the off-canvas nav/accounts panel on phones (hamburger). */
  onMenuToggle?: () => void
}

export function TopBar({ active, onNavigate, onCmdK, onMenuToggle }: Props) {
  const { data: me } = useMe()
  const navItems = visibleNavItems(me?.role)
  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar-menu-btn"
        onClick={onMenuToggle}
        aria-label="Меню"
      >
        <Icon name="menu" size={18} />
      </button>
      <div className="topbar-brand">
        <span className="topbar-brand-mark">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M2 3.5h10M2 7h7M2 10.5h8"
              stroke="#1A1408"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <span className="topbar-brand-name">Finickly</span>
        <span className="topbar-brand-ver">v2.4</span>
      </div>
      <nav className="topnav">
        {navItems.map((it) => (
          <button
            key={it.id}
            type="button"
            className={`topnav-item ${active === it.id ? 'active' : ''}`}
            onClick={() => onNavigate(it.id)}
          >
            <Icon name={it.icon} size={15} />
            <span>{it.label}</span>
            {it.badge && (
              <span className={`badge ${it.badgeKind ?? ''}`}>{it.badge}</span>
            )}
          </button>
        ))}
      </nav>
      <div className="topbar-search">
        <button type="button" className="topbar-search-btn" onClick={onCmdK}>
          <Icon name="search" size={13} color="var(--fg-4)" />
          <span>{T.buttons.search}</span>
          <span className="kbd" style={{ marginLeft: 32 }}>
            ⌘K
          </span>
        </button>
        <IconButton icon="bell" tooltip="Сповіщення" />
        <UserSummary />
      </div>
    </header>
  )
}

function UserSummary() {
  const { user } = useUser()
  const { data: me } = useMe()
  const fullName = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? '…'
  const email = me?.email ?? user?.primaryEmailAddress?.emailAddress ?? ''
  const role = me?.role ?? 'user'

  return (
    <div className="topbar-user">
      <UserButton />
      <span className="avatar-meta">
        <div className="avatar-name">{fullName}</div>
        <div className="avatar-mail">
          {email}
          {role !== 'user' && (
            <span style={{ marginLeft: 6, color: 'var(--accent)' }}>· {roleLabel(role)}</span>
          )}
        </div>
      </span>
    </div>
  )
}
